import React from "react";
import PropTypes from "prop-types";
import { Card, CardBody, CardHeader, Table, Input, Button, Badge, Spinner } from "reactstrap";
import { MEMBER_STATUS_LABELS } from "../../../../constants/members";
import { GLOBAL_STATUS, GLOBAL_STATUS_LABELS } from "../../../../constants/common";;

/**
 * PortfolioDetail
 * Painel direito do Master-Detail: exibe os clientes do employee selecionado.
 * Permite seleção múltipla e disparo de transferência.
 */
const PortfolioDetail = ({
    employee,
    members,
    selectedMemberIds,
    onToggleMember,
    onToggleSelectAll,
    onTransferClick,
    loadingMembers
}) => {
    const allSelected = members.length > 0 && selectedMemberIds.length === members.length;
    const employeeName = employee?.fullName || `${employee?.firstName || ""} ${employee?.lastName || ""}`.trim() || "Colaborador";

    // Estado vazio: nenhum employee selecionado
    if (!employee) {
        return (
            <div className="h-100 d-flex align-items-center justify-content-center bg-light-subtle rounded-0 border border-dashed p-5">
                <div className="text-center">
                    <div className="avatar-lg mx-auto mb-4">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle display-4">
                            <i className="mdi mdi-folder-account-outline"></i>
                        </div>
                    </div>
                    <h5>Selecione um colaborador</h5>
                    <p className="text-muted">Escolha um colaborador na lista ao lado para visualizar sua carteira de clientes.</p>
                </div>
            </div>
        );
    }

    return (
        <Card className="border-0 shadow-sm overflow-hidden rounded-0 position-relative">
            {/* Header */}
            <CardHeader className="bg-white py-3 border-bottom sticky-top" style={{ zIndex: 10 }}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                        <div className="avatar-sm flex-shrink-0">
                            {employee.photoUrl ? (
                                <img
                                    src={employee.photoUrl}
                                    alt={employeeName}
                                    className="img-fluid rounded shadow-sm w-100 h-100"
                                    style={{ objectFit: 'cover' }}
                                />
                            ) : (
                                <span className="avatar-title bg-primary bg-gradient text-white rounded shadow-sm">
                                    <i className="mdi mdi-account-tie font-size-22"></i>
                                </span>
                            )}
                        </div>
                        <div className="ms-3 overflow-hidden">
                            <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                                <span className="text-truncate">{employeeName}</span>
                                <Badge color={employee.status === GLOBAL_STATUS.ACTIVE ? 'success' : 'danger'} pill className="font-size-10 px-2 flex-shrink-0">
                                    {GLOBAL_STATUS_LABELS[employee.status] || 'Desconhecido'}
                                </Badge>
                            </h5>
                            <p className="text-muted mb-0 small text-truncate">
                                {employee.roleName || employee.role || "Colaborador"} — Carteira de Clientes
                            </p>
                        </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                        <Button
                            color="warning"
                            disabled={selectedMemberIds.length === 0}
                            onClick={onTransferClick}
                            className="fw-semibold shadow-sm text-white d-flex align-items-center"
                        >
                            <i className="mdi mdi-account-switch-outline me-1 font-size-16"></i>
                            Transferir ({selectedMemberIds.length})
                        </Button>
                    </div>
                </div>
            </CardHeader>

            <CardBody className="p-4">
                {/* Loading */}
                {loadingMembers ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="text-muted mt-2">Carregando clientes...</p>
                    </div>
                ) : members.length === 0 ? (
                    /* Sem membros */
                    <div className="text-center py-5">
                        <div className="avatar-lg mx-auto mb-3">
                            <div className="avatar-title bg-light text-muted rounded-circle display-4">
                                <i className="mdi mdi-account-off-outline"></i>
                            </div>
                        </div>
                        <h6 className="text-muted">Nenhum cliente nesta carteira</h6>
                        <p className="text-muted small">Este colaborador não possui clientes atribuídos como consultor responsável.</p>
                    </div>
                ) : (
                    <>
                        {/* Toolbar */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="text-muted font-size-14">
                                <strong>{members.length}</strong> cliente(s) na carteira
                                {selectedMemberIds.length > 0 && (
                                    <span className="ms-2 badge bg-primary bg-opacity-10 text-primary border border-primary-subtle px-2 py-1">
                                        {selectedMemberIds.length} selecionado(s)
                                    </span>
                                )}
                            </span>
                        </div>

                        {/* Tabela */}
                        <div className="table-responsive rounded-0 border">
                            <Table className="table-hover table-centered mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: "50px" }}>
                                            <Input
                                                type="checkbox"
                                                checked={allSelected}
                                                onChange={onToggleSelectAll}
                                                className="form-check-input"
                                            />
                                        </th>
                                        <th>Cliente</th>
                                        <th>Telefone</th>
                                        <th>Email</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {members.map((member) => (
                                        <tr
                                            key={member.id}
                                            className={selectedMemberIds.includes(member.id) ? "table-primary" : ""}
                                            style={{ cursor: "pointer" }}
                                            onClick={() => onToggleMember(member.id)}
                                        >
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <Input
                                                    type="checkbox"
                                                    checked={selectedMemberIds.includes(member.id)}
                                                    onChange={() => onToggleMember(member.id)}
                                                    className="form-check-input"
                                                />
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <div className="avatar-xs flex-shrink-0">
                                                        {member.photoUrl ? (
                                                            <img
                                                                src={member.photoUrl}
                                                                alt={member.fullName}
                                                                className="img-thumbnail rounded-circle h-100 w-100 p-0"
                                                                style={{ objectFit: 'cover' }}
                                                            />
                                                        ) : (
                                                            <span className="avatar-title bg-primary bg-opacity-10 text-primary rounded-circle fw-bold font-size-12">
                                                                {(member.fullName?.charAt(0) || "C").toUpperCase()}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="fw-medium text-dark">{member.fullName || "Sem nome"}</span>
                                                </div>
                                            </td>
                                            <td className="text-muted font-size-13">
                                                {member.phone || "—"}
                                            </td>
                                            <td className="text-muted font-size-13">
                                                {member.email || "—"}
                                            </td>
                                            <td>
                                                <StatusBadge status={member.status} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </CardBody>
        </Card>
    );
};

/**
 * StatusBadge
 */
const StatusBadge = ({ status }) => {
    const colorMap = {
        active: "success",
        inactive: "secondary",
        lead: "info",
        suspended: "warning",
        cancelled: "danger"
    };
    const labelMap = MEMBER_STATUS_LABELS || {
        active: "Ativo",
        inactive: "Inativo",
        lead: "Lead",
        suspended: "Suspenso",
        cancelled: "Cancelado"
    };

    return (
        <Badge
            color={colorMap[status] || "secondary"}
            className={`font-size-10 px-2 bg-opacity-10 text-${colorMap[status] || "secondary"}`}
            style={{ backgroundColor: 'transparent' }}
            pill
        >
            {labelMap[status] || status || "—"}
        </Badge>
    );
};

PortfolioDetail.propTypes = {
    employee: PropTypes.object,
    members: PropTypes.array.isRequired,
    selectedMemberIds: PropTypes.array.isRequired,
    onToggleMember: PropTypes.func.isRequired,
    onToggleSelectAll: PropTypes.func.isRequired,
    onTransferClick: PropTypes.func.isRequired,
    loadingMembers: PropTypes.bool
};

export default PortfolioDetail;
